import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import { cn } from "../../lib/utils";
import styles from "./EvesInsights.module.css";
import { ClipboardIcon, SparklesIcon, UploadIcon } from "./icons";
import type { InsightCard } from "./insightsData";
import { HOME_INSIGHTS, type HomeInsightCard } from "./insightsData";

interface EvesInsightsProps {
	cards?: InsightCard[];
	collapsed?: boolean;
	homeInsights?: HomeInsightCard[];
}

export function EvesInsights({
	cards,
	collapsed: _collapsed,
	homeInsights,
}: EvesInsightsProps = {}) {
	const { t } = useClientTranslations("common");
	const navigate = useNavigate();
	const { logHome } = useAnalytics();

	// New Home Page design: no cards prop passed
	if (!cards) {
		return (
			<section className={styles.insightsSection}>
				<h2 className={styles.heading}>{t("home.evesInsights.heading")}</h2>
				<p className={styles.subtitle}>{t("home.evesInsights.subtitle")}</p>
				<div className={styles.cardList}>
					{(homeInsights ?? HOME_INSIGHTS).map((card) => {
						const content = (
							<>
								<img
									src={card.illustration}
									alt=""
									className={styles.cardIllustration}
								/>
								<div className={styles.cardTextBlock}>
									{card.pillKey && (
										<span className={styles.cardPill}>{t(card.pillKey)}</span>
									)}
									<span className={styles.cardTitle}>{t(card.titleKey)}</span>
									<span className={styles.cardDescription}>
										{t(card.descriptionKey)}
									</span>
								</div>
								<div className={styles.cardChevronContainer}>
									<svg
										className={styles.cardChevron}
										viewBox="0 0 24 24"
										fill="none"
										aria-hidden="true"
									>
										<path
											d="M9 18l6-6-6-6"
											stroke="#131314"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</div>
							</>
						);

						if (card.route) {
							return (
								<button
									key={card.id}
									type="button"
									className={cn(
										styles.card,
										card.highlighted && styles.cardHighlighted,
									)}
									style={{ backgroundColor: card.backgroundColor }}
									onClick={() => {
										logHome("insight_clicked", {
											card_id: card.id,
											destination: card.route,
										});
										navigate(card.route as string);
									}}
								>
									{content}
								</button>
							);
						}

						return (
							<div
								key={card.id}
								className={cn(
									styles.card,
									card.highlighted && styles.cardHighlighted,
								)}
								style={{ backgroundColor: card.backgroundColor }}
							>
								{content}
							</div>
						);
					})}
				</div>
			</section>
		);
	}

	// Legacy ChatPage design: cards prop provided
	if (cards.length === 0) {
		return null;
	}

	const handleCardAction = (card: InsightCard) => {
		if (card.action) {
			navigate(card.action);
		}
	};

	const renderActionIcon = (icon: "questionnaire" | "upload") => {
		switch (icon) {
			case "questionnaire":
				return <ClipboardIcon size={20} />;
			case "upload":
				return <UploadIcon size={20} />;
		}
	};

	return (
		<div className={styles.insightsContainer}>
			<h2 className={styles.legacyHeading}>{t("evesInsights.heading")}</h2>
			{cards.map((card) => {
				if (card.type === "action") {
					return (
						<button
							key={card.id}
							type="button"
							className={styles.actionCard}
							onClick={() => handleCardAction(card)}
						>
							<span className={styles.actionCardText}>{t(card.textKey)}</span>
							<span className={styles.actionCardIconButton}>
								{renderActionIcon(card.icon)}
							</span>
						</button>
					);
				}
				const infoContent = (
					<>
						<span className={styles.infoCardIcon}>
							<SparklesIcon size={16} />
						</span>
						<span className={styles.infoCardText}>{t(card.textKey)}</span>
					</>
				);

				if (card.action) {
					return (
						<button
							key={card.id}
							type="button"
							className={cn(styles.infoCard, styles.infoCardClickable)}
							onClick={() => handleCardAction(card)}
						>
							{infoContent}
						</button>
					);
				}

				return (
					<div key={card.id} className={styles.infoCard}>
						{infoContent}
					</div>
				);
			})}
		</div>
	);
}
